import React, { useEffect, useState, useCallback, useRef } from 'react';
import { quotations as qApi, orders as ordersApi, agentDocuments as docsApi } from '../utils/api';
import { useToast, ToastContainer } from '../hooks/useToast';
import { gridLines, isTechPack } from '../utils/sizeGrid';

const ORDER_STATUS_LIST = [
  'Order Placed','Sample Sent','Sample Approved','In Production',
  'Quality Check','Shipped','In Customs','Delivered',
];

const STATUS_BADGE = {
  'Order Placed':     { bg:'#EFF6FF', color:'#1D4ED8' },
  'Sample Sent':      { bg:'#FFFBEB', color:'#B45309' },
  'Sample Approved':  { bg:'#F0FDF4', color:'#15803D' },
  'In Production':    { bg:'#FFF7ED', color:'#C2410C' },
  'Quality Check':    { bg:'#F5F3FF', color:'#6D28D9' },
  'Shipped':          { bg:'#EFF6FF', color:'#1E40AF' },
  'In Customs':       { bg:'#FEF9C3', color:'#854D0E' },
  'Delivered':        { bg:'#F0FDF4', color:'#166534' },
};

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; }
}

function fmtUSD(v) {
  if (!v && v !== 0) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ───────────────────────────────────────────────
// Detail view for a single order
// ───────────────────────────────────────────────
function AgentOrderDetail({ id, onBack }) {
  const [order, setOrder]     = useState(null);
  const [docs, setDocs]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdv]   = useState(false);
  const [uploading, setUpl]   = useState(false);
  const [desc, setDesc]       = useState('');
  const { toasts, toast }     = useToast();
  const fileRef               = useRef();

  const loadOrder = useCallback(async () => {
    try {
      const [o, d] = await Promise.all([qApi.get(id), docsApi.list(id)]);
      setOrder(o);
      setDocs(d);
    } catch { toast('Erro ao carregar pedido', 'error'); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  async function handleAdvance() {
    if (!order) return;
    const idx = ORDER_STATUS_LIST.indexOf(order.status);
    if (idx < 0 || idx >= ORDER_STATUS_LIST.length - 1) return;
    const next = ORDER_STATUS_LIST[idx + 1];
    if (!window.confirm(`Avançar status para "${next}"?`)) return;
    setAdv(true);
    try {
      await qApi.setStatus(id, next);
      toast(`Status atualizado para "${next}"`, 'success');
      loadOrder();
    } catch(e) { toast(e.response?.data?.error || 'Erro', 'error'); }
    finally { setAdv(false); }
  }

  async function handleUpload(e) {
    const file = e.target.files[0];
    if (!file) return;
    setUpl(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('description', desc);
      await docsApi.upload(id, fd);
      toast('Documento enviado!', 'success');
      setDesc('');
      if (fileRef.current) fileRef.current.value = '';
      loadOrder();
    } catch(e) { toast(e.response?.data?.error || 'Erro ao enviar', 'error'); }
    finally { setUpl(false); }
  }

  async function handleDeleteDoc(docId) {
    if (!window.confirm('Remover este documento?')) return;
    try { await docsApi.delete(docId); toast('Removido', 'default'); loadOrder(); }
    catch { toast('Erro ao remover', 'error'); }
  }

  if (loading) return (
    <div className="page" style={{ display:'flex', alignItems:'center', justifyContent:'center', minHeight:300 }}>
      <div style={{ color:'var(--gray-400)' }}>Carregando...</div>
    </div>
  );
  if (!order) return null;

  const statusIdx   = ORDER_STATUS_LIST.indexOf(order.status);
  const canAdvance  = statusIdx >= 0 && statusIdx < ORDER_STATUS_LIST.length - 1;
  const badge       = STATUS_BADGE[order.status] || { bg:'var(--gray-100)', color:'var(--gray-700)' };

  return (
    <div className="page">
      <ToastContainer toasts={toasts} />

      {/* Header */}
      <div className="page-header">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={onBack} style={{ marginBottom: 8 }}>← Voltar</button>
          <div className="page-title">{order.order_type === 'sample' ? '🧪 Pedido de Amostra' : '📦 Pedido Bulk'} — {order.reference}</div>
          <div className="page-subtitle">{order.supplier_name} · {order.collection || 'Sem coleção'}</div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ padding:'6px 14px', borderRadius:20, fontSize:13, fontWeight:600, background: badge.bg, color: badge.color }}>
            {order.status}
          </span>
          {canAdvance && (
            <button className="btn btn-primary" onClick={handleAdvance} disabled={advancing}>
              {advancing ? 'Atualizando...' : `→ ${ORDER_STATUS_LIST[statusIdx + 1]}`}
            </button>
          )}
        </div>
      </div>

      {/* Status pipeline */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display:'flex', gap:0, overflowX:'auto', padding:'4px 0' }}>
          {ORDER_STATUS_LIST.map((s, i) => {
            const done    = i < statusIdx;
            const current = i === statusIdx;
            return (
              <div key={s} style={{ display:'flex', alignItems:'center', flex:'1 1 0', minWidth:90 }}>
                <div style={{
                  flex:1, padding:'8px 4px', textAlign:'center', fontSize:11, fontWeight: current ? 700 : 400,
                  color: done ? 'var(--pantone)' : current ? 'white' : 'var(--gray-400)',
                  background: done ? 'var(--accent-light)' : current ? 'var(--pantone)' : 'var(--gray-50)',
                  borderRadius: i === 0 ? '8px 0 0 8px' : i === ORDER_STATUS_LIST.length-1 ? '0 8px 8px 0' : 0,
                  borderTop: `2px solid ${done||current ? 'var(--pantone)' : 'var(--gray-200)'}`,
                }}>
                  {done ? '✓ ' : ''}{s}
                </div>
                {i < ORDER_STATUS_LIST.length - 1 && (
                  <div style={{ width:0, height:0, borderTop:'18px solid transparent', borderBottom:'18px solid transparent',
                    borderLeft:`10px solid ${done ? 'var(--accent-light)' : current ? 'var(--pantone)' : 'var(--gray-50)'}`,
                    position:'relative', zIndex:1, flexShrink:0 }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Items */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><div className="card-title">📋 Itens do Pedido</div></div>
        <div style={{ padding: 0, overflow:'hidden' }}>
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Produto</th>
                <th>Especificação</th>
                <th>Qty</th>
                <th>FOB Unit</th>
                <th>FOB Total</th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((item, i) => (
                <tr key={item.id}>
                  <td style={{ color:'var(--gray-400)', fontSize:12 }}>{i+1}</td>
                  <td style={{ fontWeight:600 }}>
                    {item.product}
                    {item.is_garment ? gridLines(item).map((line, li) => (
                      <div key={li} style={{ fontSize:11, fontWeight:500, color:'var(--gray-500)', fontFamily:'var(--font-mono)' }}>{li === 0 ? '👗 ' : ''}{line}</div>
                    )) : null}
                    {(item.files || []).filter(isTechPack).map(f => (
                      <div key={f.id}><a href={`/uploads/${f.filename}`} target="_blank" rel="noreferrer" style={{ fontSize:11, color:'var(--pantone)' }}>📐 Tech pack: {f.original_name}</a></div>
                    ))}
                  </td>
                  <td style={{ fontSize:12, color:'var(--gray-600)' }}>{item.specification || '—'}</td>
                  <td>{item.quantity || '—'}</td>
                  <td style={{ fontFamily:'var(--font-mono)' }}>{fmtUSD(item.negotiated_price)}</td>
                  <td style={{ fontFamily:'var(--font-mono)', fontWeight:600, color:'var(--pantone)' }}>
                    {item.negotiated_price && item.quantity ? fmtUSD(item.negotiated_price * item.quantity) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Documents upload */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">📎 Documentos do Agente</div>
        </div>
        <div style={{ padding: '16px 20px' }}>
          {/* Upload area */}
          <div style={{ display:'flex', gap:10, alignItems:'flex-end', marginBottom: docs.length ? 20 : 0, flexWrap:'wrap' }}>
            <div className="form-group" style={{ flex:'1 1 260px', marginBottom:0 }}>
              <label>Arquivo</label>
              <input type="file" ref={fileRef} onChange={handleUpload} disabled={uploading}
                accept="image/*,.pdf,.xlsx,.xls,.doc,.docx,.zip"
                style={{ cursor:'pointer' }}
              />
            </div>
            <div className="form-group" style={{ flex:'2 1 300px', marginBottom:0 }}>
              <label>Descrição (opcional)</label>
              <input value={desc} onChange={e=>setDesc(e.target.value)} placeholder="Ex: Fotos de produção – semana 23" disabled={uploading} />
            </div>
          </div>
          {uploading && <div style={{ fontSize:12, color:'var(--gray-400)', marginBottom:16 }}>⏳ Enviando...</div>}

          {/* Document list */}
          {docs.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>Arquivo</th>
                  <th>Descrição</th>
                  <th>Data</th>
                  <th style={{ width:60 }}></th>
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => (
                  <tr key={doc.id}>
                    <td>
                      <a href={`/uploads/${doc.filename}`} target="_blank" rel="noreferrer"
                        style={{ color:'var(--pantone)', fontWeight:600, fontSize:13 }}>
                        {doc.file_type === 'image' ? '🖼️ ' : '📄 '}{doc.original_name || doc.filename}
                      </a>
                    </td>
                    <td style={{ fontSize:12, color:'var(--gray-600)' }}>{doc.description || '—'}</td>
                    <td style={{ fontSize:12, color:'var(--gray-400)' }}>{fmtDate(doc.uploaded_at)}</td>
                    <td>
                      <button className="btn btn-ghost btn-sm" style={{ color:'var(--error)' }}
                        onClick={() => handleDeleteDoc(doc.id)}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {docs.length === 0 && !uploading && (
            <div style={{ textAlign:'center', color:'var(--gray-400)', fontSize:13, padding:'16px 0' }}>
              Nenhum documento enviado ainda. Faça upload de fotos de produção, packing list, etc.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────
// Main portal — list of quotations OR orders
// ───────────────────────────────────────────────
export default function AgentPortal({ mode = 'quotations' }) {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const { toasts, toast }         = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = mode === 'orders' ? { type: 'orders', limit: 200 } : { type: 'quotations', limit: 200 };
      const data = await qApi.list(params);
      setRows(data.rows || data);
    } catch { toast('Erro ao carregar', 'error'); }
    finally { setLoading(false); }
  }, [mode]);

  useEffect(() => { load(); setSelected(null); }, [load]);

  if (selected) return <AgentOrderDetail id={selected} onBack={() => { setSelected(null); load(); }} />;

  const title  = mode === 'orders' ? 'Meus Pedidos' : 'Minhas Cotações';
  const icon   = mode === 'orders' ? '📦' : '📋';
  const empty  = mode === 'orders' ? 'Nenhum pedido atribuído a você' : 'Nenhuma cotação atribuída a você';

  return (
    <div className="page">
      <ToastContainer toasts={toasts} />
      <div className="page-header">
        <div>
          <div className="page-title">{icon} {title}</div>
          <div className="page-subtitle">{rows.length} registro{rows.length !== 1 ? 's' : ''}</div>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow:'hidden' }}>
        {loading ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--gray-400)' }}>Carregando...</div>
        ) : rows.length === 0 ? (
          <div style={{ padding:40, textAlign:'center', color:'var(--gray-400)' }}>{empty}</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Referência</th>
                <th>Fornecedor</th>
                <th>Coleção</th>
                <th>Itens</th>
                {mode === 'orders' && <th>Tipo</th>}
                <th>Status</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const badge = STATUS_BADGE[r.status] || { bg:'var(--gray-100)', color:'var(--gray-700)' };
                return (
                  <tr key={r.id} style={{ cursor:'pointer' }} onClick={() => setSelected(r.id)}>
                    <td style={{ fontFamily:'var(--font-mono)', fontWeight:700, color:'var(--pantone)' }}>{r.reference}</td>
                    <td>{r.supplier_name}</td>
                    <td style={{ color:'var(--gray-600)', fontSize:13 }}>{r.collection || '—'}</td>
                    <td style={{ color:'var(--gray-500)' }}>{r.item_count || 0}</td>
                    {mode === 'orders' && (
                      <td>
                        <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, background: r.order_type==='sample' ? '#FFF7ED' : '#EFF6FF', color: r.order_type==='sample' ? '#C2410C' : '#1D4ED8', fontWeight:600 }}>
                          {r.order_type === 'sample' ? 'Sample' : 'Bulk'}
                        </span>
                      </td>
                    )}
                    <td>
                      <span style={{ fontSize:12, padding:'3px 10px', borderRadius:12, background: badge.bg, color: badge.color, fontWeight:600 }}>
                        {r.status}
                      </span>
                    </td>
                    <td style={{ fontSize:12, color:'var(--gray-400)' }}>{fmtDate(r.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
